import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Person as PersonIcon,
  Event as EventIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import QRScanner from '../../components/QRScanner';
import rsvpService from '../../services/rsvpService';

const QRScan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scanning, setScanning] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Check if we have a token from URL redirect
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Auto-process the token
      handleScanSuccess(token);
    }
  }, [searchParams]);

  const handleScanSuccess = useCallback(async (decodedText) => {
    if (processing) return; // Prevent multiple scans

    try {
      setProcessing(true);
      setError('');
      setScanning(false);

      // Check if it's a URL (new format) or encrypted data (old format)
      let qrData = decodedText;

      // If it's a URL, extract the token
      if (decodedText.startsWith('http')) {
        try {
          const url = new URL(decodedText);
          const pathParts = url.pathname.split('/');
          qrData = pathParts[pathParts.length - 1]; // Get the token from /checkin/{token}
        } catch (urlError) {
          console.error('Failed to parse URL:', urlError);
        }
      }

      // Call API to verify QR code (supports both token and old encrypted format)
      const response = await rsvpService.verifyQRCode(qrData);

      if (response.success) {
        setResult({
          success: true,
          alreadyCheckedIn: response.alreadyCheckedIn || false,
          message: response.message,
          data: response.data.rsvp
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify QR code');
      setResult({
        success: false,
        message: err.response?.data?.message || 'Failed to verify QR code'
      });
    } finally {
      setProcessing(false);
    }
  }, [processing]);

  const handleScanError = useCallback((err) => {
    // Ignore routine scanning errors
    console.log('Scan error:', err);
  }, []);

  const handleScanAnother = () => {
    setResult(null);
    setError('');
    setScanning(true);
    setProcessing(false);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box mb={4} display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/usher/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Box mb={3}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            QR Code Check-In
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Scan attendee QR codes to check them in
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* QR Scanner */}
        {scanning && !result && (
          <QRScanner
            onScanSuccess={handleScanSuccess}
            onScanError={handleScanError}
          />
        )}

        {/* Processing Indicator */}
        {processing && (
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying QR Code...</Typography>
          </Paper>
        )}

        {/* Success Result */}
        {result && result.success && (
          <Card elevation={3}>
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <CheckIcon
                  sx={{
                    fontSize: 80,
                    color: result.alreadyCheckedIn ? 'warning.main' : 'success.main',
                    mb: 2
                  }}
                />
                <Typography variant="h5" gutterBottom fontWeight="bold" align="center">
                  {result.alreadyCheckedIn ? 'Already Checked In' : 'Check-In Successful!'}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center">
                  {result.message}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Attendee Details */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6">Attendee Information</Typography>
                </Box>

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {result.data.attendeeName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {result.data.email}
                  </Typography>
                  {result.data.additionalGuests > 0 && (
                    <Chip
                      label={`+${result.data.additionalGuests} guest${result.data.additionalGuests > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Event Details */}
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <EventIcon color="primary" />
                  <Typography variant="h6">Event Information</Typography>
                </Box>

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    {result.data.event?.eventName}
                  </Typography>
                  {result.data.event?.dates?.[0]?.startDate && (
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(result.data.event.dates[0].startDate)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Check-in Time */}
              {result.data.checkInTime && (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TimeIcon color="primary" />
                    <Typography variant="h6">Check-In Time</Typography>
                  </Box>

                  <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(result.data.checkInTime)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Total Attendees Count */}
              {result.data.totalAttendees && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="body1" align="center" color="primary.contrastText">
                    <strong>Total Attendees:</strong> {result.data.totalAttendees} {result.data.totalAttendees === 1 ? 'person' : 'people'}
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleScanAnother}
                sx={{ mt: 3 }}
              >
                Scan Another QR Code
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error Result */}
        {result && !result.success && (
          <Card elevation={3}>
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom fontWeight="bold" align="center" color="error">
                  Check-In Failed
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center">
                  {result.message}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleScanAnother}
                sx={{ mt: 3 }}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default QRScan;
